/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxfrag */
import React, { forwardRef, memo, type PropsWithChildren, type Ref, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { Link } from 'react-resource-router';
import { useMergeRefs } from 'use-callback-ref';

import { CustomItem, LinkItem } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';
import {
	TAB_HEIGHT,
	UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_PRESSED_VAR,
	UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_VAR,
	UNSAFE_HORIZONTAL_NAVIGATION_COLOR_TEXT_SUBTLE_VAR,
} from '@atlassian/navigation-kit-common';
import type { AnchorProps, RouterLinkProps } from '@atlassian/navigation-kit-common/src/types';

import AfterElements from '../../common/ui/after-elements';
import AfterElementsMoreItem from '../../common/ui/after-elements-more-item';
import BeforeTab from '../../common/ui/before-tab';
import MenuContainerWrapper from '../../common/ui/menu-container-wrapper';
import MenuWrapper from '../../common/ui/menu-wrapper';
import { RenameMenuItemComponent } from '../../common/ui/rename-menu-item';
import TabRenameForm from '../../common/ui/tab-rename-form';
import TabWrapper from '../../common/ui/tab-wrapper';
import { useTabContext } from '../../common/utils/tab-context';
import { useDraggableTabs } from '../../controllers/draggable-tabs';

// classname is required for legacy connect.js to work as it listens and overrides clicks
/* eslint-disable @atlaskit/design-system/no-unsafe-style-overrides  */
/* eslint-disable @atlaskit/ui-styling-standard/no-classname-prop  */

type NonInteractiveTabProps = PropsWithChildren<{
	as: 'div' | 'h2';
	isRenaming: boolean;
	isSelected: boolean;
}>;

const NonInteractiveTab = memo(
	forwardRef<HTMLDivElement | HTMLHeadingElement, NonInteractiveTabProps>(
		({ as: Component, children, isRenaming, isSelected }, outerRef) => (
			// eslint-disable-next-line @atlaskit/design-system/use-heading
			<Component
				// eslint-disable-next-line @atlassian/a11y/no-noninteractive-tabindex
				tabIndex={isRenaming ? undefined : 0}
				ref={outerRef}
				data-testid="navigation-kit-ui-tab.ui.link-tab.non-interactive-tab"
				aria-current="page"
				css={[
					tabStyles,
					isSelected && currentTabLabelStyles,
					fg('blu-7829_tab_nav_active_area_fix') && activeAreaPaddingStyles,
				]}
				data-delegated-focus-ring
			>
				{children}
			</Component>
		),
	),
);

export const LinkTab = memo(
	forwardRef(
		(
			{
				className,
				elemAfter,
				elemBefore,
				handleDrag,
				href,
				isDraggable = false,
				isRenaming,
				name,
				onClick,
				onRenameCancel,
				onRenameComplete,
				position,
				renderMenu,
				testId,
				type,
			}: RouterLinkProps | AnchorProps,
			outerRef: Ref<HTMLElement>,
		) => {
			const { id, isSelected, isOverflowing } = useTabContext();
			const elementRef = useRef(null);
			const mergedRefs = useMergeRefs([outerRef, elementRef]);
			const [isMenuOpen, setIsMenuOpen] = useState(false);
			const { closestEdge, dragState, isDragging } = useDraggableTabs({
				isDragEnabled: isDraggable && !isRenaming,
				isDropEnabled: true,
				position,
				id,
				handleDrag,
				isSelected,
				isOverflowing,
				elementRef,
			});

			const hasMenu = renderMenu != null;

			if (isOverflowing) {
				return (
					<div css={overflowItemWrapperStyles} data-testid={testId}>
						<MenuContainerWrapper
							isOverflowing
							isOpen={isMenuOpen}
							isSelected={isSelected}
							hasMenu={hasMenu}
							isRenaming={isRenaming}
						>
							{isRenaming ? (
								<CustomItem
									component={RenameMenuItemComponent}
									iconAfter={elemAfter}
									iconBefore={elemBefore}
									ref={mergedRefs}
								>
									<div css={hasMenu && overflowNameWrapperStyles}>
										<TabRenameForm
											defaultValue={name}
											onCancel={onRenameCancel}
											onConfirm={onRenameComplete}
										/>
									</div>
									<AfterElementsMoreItem
										elemBefore={elemBefore}
										elemAfter={elemAfter}
										closestEdge={closestEdge}
										dragState={dragState}
										name={name}
									/>
								</CustomItem>
							) : (
								<LinkItem
									testId="navigation-kit-ui-tab.ui.link-tab.link-item"
									ref={mergedRefs}
									href={href}
									aria-current={isSelected ? 'page' : undefined}
									className={className}
									onClick={onClick}
									iconBefore={elemBefore}
									iconAfter={elemAfter}
									isSelected={isSelected}
									UNSAFE_isDraggable={isDraggable}
								>
									<div css={hasMenu && overflowNameWrapperStyles}>{name}</div>
									<AfterElementsMoreItem
										elemBefore={elemBefore}
										elemAfter={elemAfter}
										closestEdge={closestEdge}
										dragState={dragState}
										name={name}
									/>
								</LinkItem>
							)}
							{hasMenu && (
								<MenuWrapper>
									<div css={actionItemsWrapperStyles}>
										{renderMenu != null &&
											renderMenu({ isOverflowing, isOpen: isMenuOpen, setIsOpen: setIsMenuOpen })}
									</div>
								</MenuWrapper>
							)}
						</MenuContainerWrapper>
					</div>
				);
			}

			const renderTabContent = () => {
				const inside = (
					<>
						<BeforeTab elementBefore={elemBefore} />
						{isRenaming ? (
							<TabRenameForm
								defaultValue={name}
								onCancel={onRenameCancel}
								onConfirm={onRenameComplete}
							/>
						) : (
							<span>{name}</span>
						)}
						<AfterElements
							elemAfter={elemAfter}
							closestEdge={closestEdge}
							dragState={dragState}
							name={name}
						/>
					</>
				);

				if (isSelected || isRenaming) {
					return (
						<NonInteractiveTab
							as={isRenaming ? 'div' : 'h2'}
							isRenaming={Boolean(isRenaming)}
							isSelected={isSelected}
						>
							{inside}
						</NonInteractiveTab>
					);
				}

				if (type === 'anchor') {
					return (
						// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
						<a
							href={href}
							className={className}
							onClick={onClick}
							css={[tabStyles, fg('blu-7829_tab_nav_active_area_fix') && activeAreaPaddingStyles]}
							data-testid="navigation-kit-ui-tab.ui.link-tab.tab-anchor"
							draggable="false"
							data-delegated-focus-ring
						>
							{inside}
						</a>
					);
				}

				return (
					// eslint-disable-next-line @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/no-static-element-interactions -- See https://go/a11y-no-static-element-interactions for more details
					<Link
						data-delegated-focus-ring
						data-testid="navigation-kit-ui-tab.ui.link-tab"
						to={href}
						prefetch="hover"
						css={[tabStyles, fg('blu-7829_tab_nav_active_area_fix') && activeAreaPaddingStyles]}
						className={className}
						onClick={onClick}
						draggable="false"
					>
						{inside}
					</Link>
				);
			};

			return (
				<div ref={mergedRefs as Ref<HTMLDivElement>} data-testid={testId}>
					<MenuContainerWrapper
						isSelected={isSelected}
						isOpen={isMenuOpen}
						hasMenu={hasMenu}
						isRenaming={isRenaming}
					>
						<TabWrapper isSelected={isSelected} isDragging={isDragging}>
							{renderTabContent()}

							{hasMenu && renderMenu != null && (
								<MenuWrapper key="menu">
									{renderMenu({ isOverflowing, isOpen: isMenuOpen, setIsOpen: setIsMenuOpen })}
								</MenuWrapper>
							)}
						</TabWrapper>
					</MenuContainerWrapper>
				</div>
			);
		},
	),
);

/* eslint-enable @atlaskit/design-system/no-unsafe-style-overrides */
/* eslint-enable @atlaskit/ui-styling-standard/no-classname-prop */
const currentTabLabelStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	color: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_VAR}, ${token('color.link')})`,
});

const tabStyles = css({
	display: 'flex',
	paddingBlockStart: token('space.200'),
	paddingInlineEnd: token('space.050'),
	paddingBlockEnd: token('space.200'),
	paddingInlineStart: token('space.0'),
	margin: 0,
	textDecoration: 'none',
	fontWeight: token('font.weight.medium'),
	// formerly fontSize from @atlassian/jira-common-styles"
	// can't use the token becuase it sets things we don't want
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: `14px`,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	letterSpacing: 'normal',
	whiteSpace: 'nowrap',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	height: `${TAB_HEIGHT}px`,
	boxSizing: 'border-box',
	minWidth: 'max-content',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	color: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_TEXT_SUBTLE_VAR}, ${token(
		'color.text.subtle',
	)})`,
	alignItems: 'center',
	gap: token('space.050', '4px'),
	'&:hover, &:focus-visible, &:focus, &:active': {
		boxShadow: 'none',
		textDecoration: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		color: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_VAR}, ${token('color.link')})`,
	},
	'&:active': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		color: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_PRESSED_VAR}, ${token(
			'color.link.pressed',
		)})`,
		'&::after': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			backgroundColor: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_PRESSED_VAR}, ${token(
				'color.link.pressed',
			)})`,
		},
	},
	'&:focus, &:focus-visible': {
		outline: 'none',
	},
});

const activeAreaPaddingStyles = css({
	// Adding paddingLeft from TabWrapper to existing paddingInlineEnd to prevent gap between active area and tab
	paddingInlineEnd: `calc(${token('space.075')} + ${token('space.050')})`,
	paddingInlineStart: token('space.075'),
});

const overflowItemWrapperStyles = css({
	position: 'relative',
});

const overflowNameWrapperStyles = css({
	// These are there to ensure there is space for the menu. The 12px represents the gap between the name and menu,
	// which matches the gap for an icon after from the menu item styles, and the 24px is the width of the icon
	paddingRight: token('space.150', '12px'),
	marginRight: token('space.300', '24px'),
});

const actionItemsWrapperStyles = css({
	position: 'absolute',
	display: 'flex',
	alignItems: 'center',
	right: token('space.200', '16px'),
	top: token('space.100', '8px'),
	gap: token('space.050', '4px'),
});

export default LinkTab;
